import { FC, useCallback, useEffect, useState, useMemo } from 'react';
import { isEmpty } from 'lodash';
import { Space, Spin } from 'antd';
import { NotificationContextProvider } from 'context/NotificationContext';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import iconEdit from 'images/icon-edit.svg';
import {
  findFrequencyByBenefitKind,
  getAgeBandedPlans,
  getListedMultiplePlanSuperScripts,
} from 'modules/benefitsWellness/util';
import MedicalCardWrapper from 'modules/benefitsWellness/components/MedicalCardWrapper/MedicalCardWrapper';
import MDVPlansWrapper from 'modules/benefitsWellness/components/MDVPlansWrapper/MDVPlansWrapper';
import MedicalPlansChannel from 'modules/clients/UPClient/channels/MedicalPlansChannel';
import AddOrRemovePlans from 'modules/benefitsWellness/components/AddOrRemovePlans/AddOrRemovePlans';
import TableContextProvider from 'modules/benefitsWellness/components/TableContext/TableContextProvider';
import NoMDVPlansView from 'components/NoPlansView/NoMDVPlansView';
import AlertMessage from 'components/AlertMessage/AlertMessage';
import MDVTypes from 'modules/benefitsWellness/enums/MDVTypes';
import useUPClient from 'modules/clients/UPClient/useUPClient';
import {
  getCustomFieldsToState,
  getMedicalPlanList,
  saveAndReloadMedicalSection,
} from 'modules/benefitsWellness/slices/medicalSlice';
import useBenguide from 'hooks/benguide';
import MDVPlan from 'model/MDVPlan';
import {
  AGE_BAND,
  FOUR_TIER,
  N_TIER,
} from 'modules/benefitsWellness/constants/BenefitConstants';
import {
  MEDICAL_PLAN_CHANNEL,
  OPEN_PLAN_LABELS_MODAL,
} from 'modules/clients/UPClient/UPClientConst';
import { MDVPlanSuperscript } from 'model/LableTypes';
import { BENEFIT_CATEGORY } from 'constants/commonConstants';
import Rate from 'model/Rate';
import EditableHotspot from 'components/EditableHotspot/EditableHotspot';
import LinkButton from 'components/buttons/LinkButton/LinkButton';
import BenefitGuideRefreshChannel from 'modules/clients/UPClient/channels/BenefitGuideRefreshChannel';
import BenefitGuideNotificationsChannel from 'modules/clients/UPClient/channels/BenefitGuideNotificationsChannel';
import { PLAN_RECOMMENDER } from 'modules/home/constants';
import { planRecommenderToggle } from 'services/BenGuideService';
import FooterTextSection from 'components/FooterTextSection';
import { checkBenefitGuidesRates } from 'modules/benefitsWellness/slices/benefitWellnessSlice';

import styles from './medical.module.less';

type MedicalProps = {
  onCloseContributions?: Function;
};

const Medical: FC<MedicalProps> = (props: MedicalProps) => {
  const upClient = useUPClient();
  const { onCloseContributions = () => {} } = props;
  const { medical } = useAppSelector((state: any) => state);
  const benGuide = useAppSelector((state) => state.benguide.data);
  const { rateValidations, rateValidationInProgress } = useAppSelector(
    (state) => state.benefitWellness
  );
  const [isSaveDisable, setSaveDisable] = useState<boolean>(false);
  const [isAlertVisible, setIsAlertVisible] = useState<boolean>(false);
  const [isOpenContributionModal, setIsOpenContributionModal] =
    useState<boolean>(false);
  const [contributionModalClose, setContributionModalClose] =
    useState<boolean>(false);
  const [isChanged, setIsChanged] = useState<boolean>(false);
  const [planListUpdateFlag, setPlanListUpdateFlag] = useState<boolean>(false);

  const { planList, medicalSection = [] } = medical;
  const { plans } = medicalSection;
  const dispatch = useAppDispatch();
  const benguide = useBenguide();
  const { masterId, planYearId, employerId, benefitClasses } = benguide;
  const isEditMode = benguide.isEditMode;
  const updatePlanList = async (
    planList: MDVPlan[],
    orderByPlanNames: boolean,
    updatedPlans: boolean
  ) => {
    setPlanListUpdateFlag(!planListUpdateFlag);
    const enabled = {
      enabled: false,
    };
    if (updatedPlans) {
      await planRecommenderToggle(masterId, PLAN_RECOMMENDER, enabled);
    }
    dispatch(saveAndReloadMedicalSection(masterId, planList, orderByPlanNames));
  };
  const contribFreq = findFrequencyByBenefitKind(
    benguide.contributionFrequencies,
    BENEFIT_CATEGORY.MEDICAL.value
  );
  const { enabled } = contribFreq;

  const enablePlanRecommender = plans.length > 1;

  useEffect(() => {
    dispatch(
      checkBenefitGuidesRates(benguide.masterId, MDVTypes.MEDICAL.toUpperCase())
    );
    dispatch(getCustomFieldsToState());
  }, [dispatch, benguide?.masterId]);

  useEffect(() => {
    setContributionModalClose(contributionModalClose);
  }, [contributionModalClose]);

  const reloadAllMedicalPlans = useCallback(() => {
    dispatch(getMedicalPlanList(employerId, planYearId, benefitClasses));
  }, [employerId, planYearId, benefitClasses, dispatch]);

  useEffect(() => {
    onCloseContributions(contributionModalClose);
  }, [contributionModalClose, onCloseContributions]);

  const foundRates =
    (!isEmpty(plans) &&
      plans
        .map((plan: MDVPlan) => plan.rates)
        .filter((plan: MDVPlan) => plan)
        .map((rate: Rate) => {
          return Object.values(rate);
        })
        .reduce((arr1: [], arr2: []) => {
          return arr1.concat(arr2);
        }, [])
        .map((rate: Rate) => rate.type)) ||
    [];

  const rateType = foundRates.includes(N_TIER)
    ? N_TIER
    : foundRates.includes(FOUR_TIER)
    ? FOUR_TIER
    : AGE_BAND;

  const taxADVTaggedPlans =
    (!isEmpty(plans) &&
      plans.filter((plan: MDVPlan) =>
        plan?.hsaMedicalContributionVO?.every(
          (contribution) =>
            contribution?.benefitKind === 'HSA' ||
            contribution?.benefitKind === 'HRA'
        )
      )) ||
    [];

  const planLabels = useMemo(
    () => getListedMultiplePlanSuperScripts(plans, 'MEDICAL'),
    [plans]
  );

  const nTierCounts = useMemo(
    () =>
      plans.reduce((nTireMax: number, plan: MDVPlan) => {
        // Handle case where plan.rates is null or undefined
        const rates = plan.rates ? Object.values(plan.rates) : [];
        const contributionsCount = rates.map((rate: any) => {
          return rate?.ntierContributions?.contributions
            ? rate.ntierContributions.contributions.length
            : 0;
        });
        const planMax = Math.max(0, ...contributionsCount);
        return Math.max(nTireMax, planMax);
      }, 0),
    [plans]
  );

  const constructDataForAddPlanLabelModal = (plans: MDVPlan[]) => {
    return {
      employerId: benGuide.employerId,
      planYear: {
        id: benGuide.planYearId,
      },
      planYearId: benGuide.planYearId,
      planIds: plans.map((p) => p?.id),
    };
  };

  const renderPlanLabels = (
    plans: MDVPlan[],
    planLabels: MDVPlanSuperscript[]
  ) => {
    const hasAnyPlanLabelParameters = plans?.some(
      (plan: MDVPlan) => plan?.planLabels?.parameters.length || 0 > 0
    );
    return (
      <>
        <div className={styles.bottomText}>
          {plans?.length > 0 &&
            (planLabels?.length > 0 ? (
              <EditableHotspot
                editText="Edit"
                editIcon={<img src={iconEdit} alt="edit" />}
                alwaysVisible={false}
                className={styles.hotSpot}
                useChannel={{
                  client: upClient,
                  channel: MEDICAL_PLAN_CHANNEL,
                  event: OPEN_PLAN_LABELS_MODAL,
                  data: constructDataForAddPlanLabelModal(plans),
                }}
              >
                {planLabels?.map(
                  (
                    { labelName, superscript }: MDVPlanSuperscript,
                    index: number
                  ) => (
                    <div key={index}>
                      <sup>{superscript}</sup>
                      {labelName}
                    </div>
                  )
                )}
              </EditableHotspot>
            ) : isEditMode ? (
              <EditableHotspot
                alwaysVisible={true}
                useChannel={{
                  client: upClient,
                  channel: MEDICAL_PLAN_CHANNEL,
                  event: OPEN_PLAN_LABELS_MODAL,
                  data: constructDataForAddPlanLabelModal(plans),
                }}
              >
                <LinkButton onClick={() => {}}>
                  {hasAnyPlanLabelParameters ? 'Manage Labels' : 'Create Label'}
                </LinkButton>
              </EditableHotspot>
            ) : (
              <></>
            ))}
        </div>
        {plans?.length > 0 && (
          <FooterTextSection masterId={masterId} isEditMode={isEditMode} />
        )}
      </>
    );
  };

  const showAlert = () => {
    setIsAlertVisible(true);
  };

  const getAlert = () => (
    <AlertMessage
      className={styles.alert}
      type="success"
      message={'Successfully created new plan.'}
      closeAlert={() => setIsAlertVisible(false)}
      wrapperClassName={styles.planAlertWrapper}
    />
  );

  return (
    <NotificationContextProvider isDisableSave={isSaveDisable}>
      {isAlertVisible && getAlert()}
      <div className={styles.medicalWrapper}>
        <MedicalPlansChannel />
        <BenefitGuideRefreshChannel benefitKind={MDVTypes.MEDICAL} />
        <BenefitGuideNotificationsChannel
          setDisableSave={() => setSaveDisable(true)}
        />
        <MDVPlansWrapper
          planType={MDVTypes.MEDICAL}
          addedPlansCount={plans.length}
          enablePlanRecommender={enablePlanRecommender}
          showPlanRecommender={plans.length > 0}
          disablePlanRecommenderOnEdit={!(plans.length > 0)}
          rateType={rateType}
          onView={setIsOpenContributionModal}
          hasMismatchContributions={
            isEmpty(taxADVTaggedPlans)
              ? rateValidations.hasMismatchContributions ||
                !enabled ||
                getAgeBandedPlans(plans).length === plans.length
              : false
          }
          closeContributions={contributionModalClose}
          isUpdated={isChanged}
          planListUpdateFlag={planListUpdateFlag}
          nTiersCount={
            foundRates.includes(N_TIER) &&
            foundRates.includes(FOUR_TIER) &&
            nTierCounts < 4
              ? 4
              : nTierCounts
          }
        />
        {plans.length > 0 ? (
          rateValidationInProgress ? (
            <Space size="middle">
              <Spin size="large" />
            </Space>
          ) : (
            <TableContextProvider>
              <MedicalCardWrapper
                onUpdatePlanList={updatePlanList}
                allPlans={planList.data}
                reloadPlans={reloadAllMedicalPlans}
                showAlert={showAlert}
                isContributionsViewOpen={isOpenContributionModal}
                isLoading={planList.inProgress}
                onCloseContributions={setContributionModalClose}
                isUpdated={setIsChanged}
              />
            </TableContextProvider>
          )
        ) : isEditMode ? (
          <AddOrRemovePlans
            visible={true}
            withoutModal={true}
            title="Medical"
            onSave={updatePlanList}
            addedPlanList={plans}
            allPlans={planList.data}
            reloadPlans={reloadAllMedicalPlans}
            isLoading={planList.inProgress}
            showAlert={showAlert}
          />
        ) : (
          <NoMDVPlansView />
        )}
        {renderPlanLabels(plans, planLabels)}
      </div>
    </NotificationContextProvider>
  );
};

export default Medical;
